import { Controller } from '@hotwired/stimulus';
import { showToast } from '../utils/toast';
import { csrfToken } from '../utils/csrf';

export default class SubscriptionFormController extends Controller {
  static targets = [
    'amountField',
    'fieldsErrors',
    'errorTemplate',
    'balancePlaceholder',
    'investModal',
    'subscribeModal',
    'warningModal',
    'subscribeCheckbox',
    'subscribeConfirmButton',
    'warningCheckbox',
    'warningConfirmButton',
  ];

  static values = {
    walletBalance: Number,
    humanWalletBalance: String,
    shareValue: Number,
    userMayHaveWarning: Boolean,
    risksAppreciationUrl: String,
    defaultAmount: Number,
    networkError: String,
  };

  #transitioning = false;

  preselectAmount(event) {
    this.amountFieldTarget.value = event.detail.amount;
  }

  resetAmount() {
    if (this.#transitioning) return;
    this.amountFieldTarget.value = this.defaultAmountValue;
  }

  submitSubscription(event) {
    event.preventDefault();
    this.#triggerConfirmModal();
  }

  preventEnterSubmit(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#triggerConfirmModal();
    }
  }

  toggleSubscribeConfirm() {
    const allChecked = this.subscribeCheckboxTargets.every((cb) => cb.checked);
    this.subscribeConfirmButtonTarget.disabled = !allChecked;
  }

  toggleWarningConfirm() {
    const allChecked = this.warningCheckboxTargets.every((cb) => cb.checked);
    this.warningConfirmButtonTarget.disabled = !allChecked;
  }

  resetSubscribeCheckboxes() {
    this.toggleSubscribeConfirm();
  }

  resetWarningCheckboxes() {
    this.toggleWarningConfirm();
  }

  #triggerConfirmModal() {
    const amount = parseInt(this.amountFieldTarget.value);

    if (!this.amountFieldTarget.reportValidity()) return;

    if (this.walletBalanceValue - amount < 0) {
      const clone = this.errorTemplateTarget.cloneNode(true);
      clone.querySelector('[data-subscription-form-target="balancePlaceholder"]').textContent =
        this.humanWalletBalanceValue;
      const fragment = document.createDocumentFragment();
      fragment.append(...clone.childNodes);
      showToast('error', fragment);
      return;
    }

    if (this.userMayHaveWarningValue) {
      this.#checkRisksAppreciation(amount);
    } else {
      this.#showSubscribeModal();
    }
  }

  #checkRisksAppreciation(amount) {
    const url = new URL(this.risksAppreciationUrlValue, window.location.origin);
    url.searchParams.append('amount', amount);

    fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');

        return response.json();
      })
      .then((data) => {
        if (data.warning_modal_required) {
          this.#showWarningModal();
        } else {
          this.#showSubscribeModal();
        }
      })
      .catch(() => {
        this.fieldsErrorsTarget.textContent = this.networkErrorValue;
      });
  }

  #showSubscribeModal() {
    this.#switchModal(this.subscribeModalTarget);
  }

  #showWarningModal() {
    this.#switchModal(this.warningModalTarget);
  }

  #switchModal(nextModalElement) {
    this.#transitioning = true;
    this.investModalTarget.addEventListener('hidden.bs.modal', () => {
      this.#transitioning = false;
      bootstrap.Modal.getOrCreateInstance(nextModalElement).show();
    }, { once: true });
    bootstrap.Modal.getOrCreateInstance(this.investModalTarget).hide();

    nextModalElement.addEventListener('hidden.bs.modal', () => {
      bootstrap.Modal.getInstance(nextModalElement)?.dispose();
    }, { once: true });
  }
}
